import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { LayoutDashboard, Users, FileText, LogOut, FilePlus, IdCard, Inbox, MessageCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import LanguageSwitcher from "./LanguageSwitcher";
import api from "../lib/api";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    if (!user) return;
    const poll = () => {
      api.get("/messages/unread-count")
        .then((r) => setUnread(r.data?.unread || 0))
        .catch(() => {});
    };
    poll();
    const iv = setInterval(poll, 30000);
    return () => clearInterval(iv);
  }, [user]);
  const sub = user?.subscription;
  const showTrialBanner = sub?.status === "trial" && sub?.days_left !== null && sub?.days_left <= 7;
  const showExpireSoon = sub?.status === "active" && sub?.days_left !== null && sub?.days_left <= 5;
  const nav = [
    { to: "/", label: t("nav.dashboard"), icon: LayoutDashboard, testId: "nav-dashboard" },
    { to: "/perfil", label: t("nav.profile"), icon: IdCard, testId: "nav-profile-link" },
    { to: "/pacientes", label: t("nav.patients"), icon: Users, testId: "nav-patients" },
    { to: "/richieste", label: t("nav.requests"), icon: Inbox, testId: "nav-requests" },
    { to: "/mensajes", label: t("nav.messages"), icon: MessageCircle, testId: "nav-messages", badge: unread },
    { to: "/plantillas", label: t("nav.templates"), icon: FilePlus, testId: "nav-templates" },
    { to: "/colleghi", label: t("nav.colleagues"), icon: Users, testId: "nav-colleagues" },
    { to: "/facturas", label: t("nav.invoices"), icon: FileText, testId: "nav-invoices" },
  ];
  return (
    <div className="min-h-screen bg-[#FAF9F6] text-[#1C1917]">
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-stone-200/60">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-10">
            <div className="flex items-baseline gap-2" data-testid="app-logo">
              <span className="font-heading text-2xl font-light tracking-tight">Ambra</span>
              <span className="text-xs uppercase tracking-[0.25em] text-stone-400">patient matter</span>
            </div>
            <nav className="hidden md:flex items-center gap-1">
              {nav.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  data-testid={n.testId}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-colors duration-300 ${
                      isActive ? "bg-stone-900 text-white" : "text-stone-600 hover:text-stone-900 hover:bg-stone-100"
                    }`
                  }
                >
                  <n.icon className="w-4 h-4" strokeWidth={1.5} />
                  {n.label}
                  {n.badge > 0 && (
                    <span data-testid={`badge-${n.testId}`}
                      className="ml-1 text-[10px] font-medium bg-[#D4A373] text-stone-900 px-1.5 py-0.5 rounded-full">
                      {n.badge}
                    </span>
                  )}
                </NavLink>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            {user?.role === "owner" && (
              <button onClick={() => navigate("/admin")} data-testid="nav-admin"
                className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-full text-xs uppercase tracking-widest bg-[#D4A373]/15 text-[#B07D4F] hover:bg-[#D4A373]/25 transition-colors">
                Admin
              </button>
            )}
            <button onClick={() => navigate("/suscripcion")} data-testid="nav-subscription"
              className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-full text-sm text-stone-600 hover:text-stone-900 hover:bg-stone-100 transition-colors">
              <span className="text-xs uppercase tracking-widest">{t("nav.subscription")}</span>
            </button>
            <div className="hidden sm:block text-right cursor-pointer" onClick={() => navigate("/perfil")} data-testid="nav-profile">
              <div className="text-sm text-stone-900 hover:text-[#B07D4F] transition-colors" data-testid="current-user-name">{user?.name}</div>
              <div className="text-xs text-stone-500">{user?.email}</div>
            </div>
            <button
              onClick={() => { logout(); navigate("/login"); }}
              data-testid="logout-btn"
              className="flex items-center gap-2 px-4 py-2 rounded-full border border-stone-200 text-sm text-stone-700 hover:bg-stone-100 transition-colors duration-300"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.5} />
              {t("nav.logout")}
            </button>
          </div>
        </div>
        {/* Mobile nav */}
        <nav className="md:hidden flex overflow-x-auto gap-1 px-4 pb-2">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"}
              className={({ isActive }) => `flex-shrink-0 px-3 py-1.5 rounded-full text-xs ${isActive ? "bg-stone-900 text-white" : "text-stone-600"}`}>
              {n.label}
            </NavLink>
          ))}
        </nav>
      </header>
      {(showTrialBanner || showExpireSoon) && (
        <div className="bg-[#D4A373]/15 border-b border-[#D4A373]/30">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-2.5 flex items-center justify-between gap-4 flex-wrap text-sm">
            <span className="text-[#7A5734]" data-testid="trial-banner">
              {showTrialBanner
                ? t("layout.trialBanner", { days: sub.days_left })
                : t("layout.expireSoonBanner", { days: sub.days_left })}
            </span>
            <button onClick={() => navigate("/precios")} data-testid="trial-upgrade-btn"
              className="px-4 py-1.5 rounded-full bg-stone-900 text-white text-xs uppercase tracking-widest hover:bg-stone-800">
              {t("layout.upgrade")}
            </button>
          </div>
        </div>
      )}
      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8 lg:py-12">{children}</main>
    </div>
  );
}
